import axios from 'axios';
import { Worker, CreateWorkerData } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'https://makicheck.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const workerService = {
  async getWorkers(): Promise<Worker[]> {
    const response = await api.get('/workers');
    return response.data;
  },

  async getWorker(id: number): Promise<Worker> {
    const response = await api.get(`/workers/${id}`);
    return response.data;
  },

  async createWorker(workerData: CreateWorkerData): Promise<Worker> {
    const response = await api.post('/workers', workerData);
    return response.data;
  },

  async updateWorker(id: number, workerData: Partial<Worker>): Promise<Worker> {
    const response = await api.put(`/workers/${id}`, workerData);
    return response.data;
  },

  async deleteWorker(id: number): Promise<void> {
    await api.delete(`/workers/${id}`);
  },

  async getWorkerTimeRecords(workerId: number, startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/time/worker/${workerId}?${params}`);
    return response.data;
  },

  async updateTimeRecord(recordId: number, timeData: {
    mark_entry?: string;
    lunch_entry?: string;
    lunch_exit?: string;
    final_exit?: string;
  }): Promise<any> {
    const response = await api.put(`/time/record/${recordId}`, timeData);
    return response.data;
  },

  async deleteTimeRecord(recordId: number): Promise<void> {
    await api.delete(`/time/record/${recordId}`);
  },

  async deleteBonus(workerId: number, bonusId: number): Promise<void> {
    await api.delete(`/time/worker/${workerId}/bonus/${bonusId}`);
  },

  async updateBonus(workerId: number, bonusId: number, data: { amount?: number; description?: string }): Promise<any> {
    const response = await api.put(`/time/worker/${workerId}/bonus/${bonusId}`, data);
    return response.data;
  },

  async updateMyCredentials(data: { new_worker_id?: string; new_pin?: string }): Promise<any> {
    const response = await api.put('/workers/me/credentials', data);
    return response.data;
  },

  async createManualTimeRecord(workerId: number, timeData: {
    date: string;
    mark_entry?: string;
    lunch_entry?: string;
    lunch_exit?: string;
    final_exit?: string;
  }): Promise<any> {
    const response = await api.post(`/time/worker/${workerId}/manual`, timeData);
    return response.data;
  },

  async addWeeklyBonus(workerId: number, bonusData: {
    week_start_date: string;
    bonus_date?: string;
    amount: number;
    description?: string;
  }): Promise<any> {
    const response = await api.post(`/time/worker/${workerId}/bonus`, bonusData);
    return response.data;
  },

  async getWeeklyBonuses(workerId: number, startDate?: string, endDate?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await api.get(`/time/worker/${workerId}/bonuses?${params}`);
    return response.data;
  },
};
